import { ChangeDetectionStrategy, Component, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { SelectButtonModule } from 'primeng/selectbutton';
import { SelectModule } from 'primeng/select';
import { FormsModule } from '@angular/forms';
import { CollectionService } from '../../core/services/collection.service';
import { CandidateSector } from '../../shared/models/candidateSector-model';
import { SectorListComponent } from '../../shared/ui/sector-list/sector-list.component';

type SortPeriod = '1mo' | '3mo' | '6mo' | 'ytd' | 'trend';

/**
 * Calculate trend metrics for a sector
 */
interface TrendMetrics {
  trend1m: number;
  trend3m: number;
  trend6m: number;
  acceleration: number;
}

function getTrendMetrics(sector: CandidateSector): TrendMetrics {
  const trend1m = sector.m1 ?? 0;
  const trend3m = (sector.m3 ?? 0) / 3;
  const trend6m = (sector.m6 ?? 0) / 6;
  const acceleration = trend1m - trend6m;

  return { trend1m, trend3m, trend6m, acceleration };
}

@Component({
  selector: 'app-sectors',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule, SelectButtonModule, SelectModule, SectorListComponent],
  templateUrl: './sectors.component.html',
  styleUrls: ['./sectors.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SectorsComponent implements OnInit {
  private collectionSvc = inject(CollectionService);

  // Raw data from API
  rawSectors = signal<CandidateSector[]>([]);
  loading = signal(true);
  error = signal<string | null>(null);

  // Sort state - default to 'trend'
  selectedSortPeriod = signal<SortPeriod>('trend');

  // Sort options for SelectButton
  readonly sortOptions = [
    { label: 'Trend', value: 'trend' as SortPeriod },
    { label: '1 Mo.', value: '1mo' as SortPeriod },
    { label: '3 Mo.', value: '3mo' as SortPeriod },
    { label: '6 Mo.', value: '6mo' as SortPeriod },
    { label: 'YTD', value: 'ytd' as SortPeriod }
  ];

  // Computed sorted sectors based on selected period
  sectors = computed(() => {
    const period = this.selectedSortPeriod();

    // Sort by selected period in descending order (highest first)
    const sectorData = [...this.rawSectors()];

    if (period === 'trend') {
      // Sort by acceleration with tie breakers
      return sectorData.sort((a, b) => {
        const metricsA = getTrendMetrics(a);
        const metricsB = getTrendMetrics(b);

        // Primary: acceleration (descending)
        if (metricsB.acceleration !== metricsA.acceleration) {
          return metricsB.acceleration - metricsA.acceleration;
        }

        // Tie breaker 1: trend1m (descending)
        if (metricsB.trend1m !== metricsA.trend1m) {
          return metricsB.trend1m - metricsA.trend1m;
        }

        // Tie breaker 2: trend3m (descending)
        if (metricsB.trend3m !== metricsA.trend3m) {
          return metricsB.trend3m - metricsA.trend3m;
        }

        // Tie breaker 3: trend6m (descending)
        if (metricsB.trend6m !== metricsA.trend6m) {
          return metricsB.trend6m - metricsA.trend6m;
        }

        // Tie breaker 4: alphabetical by sector name
        return a.sector.localeCompare(b.sector);
      });
    }

    return sectorData.sort((a, b) => {
      let valueA: number;
      let valueB: number;

      switch (period) {
        case '1mo':
          valueA = a.m1;
          valueB = b.m1;
          break;
        case '3mo':
          valueA = a.m3;
          valueB = b.m3;
          break;
        case '6mo':
          valueA = a.m6;
          valueB = b.m6;
          break;
        case 'ytd':
          valueA = a.ytd;
          valueB = b.ytd;
          break;
        default:
          return 0;
      }

      return valueB - valueA; // Descending order
    });
  });

  ngOnInit() {
    this.collectionSvc.getSectorsForCandidates().subscribe({
      next: (data) => this.rawSectors.set(data),
      error: (err) => this.error.set('Failed to load sectors'),
      complete: () => this.loading.set(false),
    });
  }

  // Clear the sort selection to return to default view (trend)
  clearSort(): void {
    this.selectedSortPeriod.set('trend');
  }
}
